import type { ConvexReactClient } from 'convex/react';
import type { Id } from '../../convex/_generated/dataModel';
import { api } from '../../convex/_generated/api';

export type UploadAssetInput = {
  file: File;
  type: string;
  name?: string;
  accountId?: string;
  ownerEntityId?: Id<'entities'>;
  ownerEntityType?: string;
  fileHash?: string;
};

export type UploadAssetResult = {
  assetId: Id<'assets'>;
  storageKey: string;
  url: string;
  width?: number;
  height?: number;
};

const getImageDimensions = async (
  file: File
): Promise<{ width: number; height: number } | null> => {
  if (!file.type.startsWith('image/')) return null;
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = new Image();
    const loaded = new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
    });
    img.src = objectUrl;
    await loaded;
    return { width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const uploadAsset = async (
  convex: ConvexReactClient,
  input: UploadAssetInput
): Promise<UploadAssetResult> => {
  const name = input.name ?? input.file.name;
  const mimeType = input.file.type || undefined;
  const result = await convex.action(
    api.assets.createUploadUrl,
    {
      accountId: input.accountId,
      fileName: name,
    }
  );
  const { storageKey, url } = result;
  const putUrl = typeof result.uploadUrl === 'string' ? result.uploadUrl : (result.uploadUrl as { url: string }).url;

  const uploadResponse = await fetch(putUrl, {
    method: 'PUT',
    body: input.file,
    headers: mimeType ? { 'Content-Type': mimeType } : undefined,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed with status ${uploadResponse.status}`);
  }

  const dimensions = await getImageDimensions(input.file);
  const assetId = await convex.mutation(api.assets.recordUpload, {
    accountId: input.accountId,
    name,
    type: input.type,
    storageKey,
    url,
    mimeType,
    width: dimensions?.width,
    height: dimensions?.height,
    ownerEntityId: input.ownerEntityId,
    ownerEntityType: input.ownerEntityType,
    fileHash: input.fileHash,
  });

  return {
    assetId,
    storageKey,
    url,
    width: dimensions?.width,
    height: dimensions?.height,
  };
};
